import {
    Container,
    Sprite,
    Texture
} from 'pixi.js';
import { UIBoxBG } from './UIBoxBG';
import { Button, NineSliceButton, FocusManager, fonts, InteractiveWrapper } from '../../ui';
import { LoadContext, Disposable, DisposableGroup, VOManager } from '../../utils';
import { SimplePoint } from './Grid';
import { GAMEPLAY, LAYOUT } from '../../constants';
import KeyboardJS from 'keyboardjs';

export const CLEAR = Symbol('Clear Input');

const KEYS: {[label:string]: string[]}  = {};
// A -> [a], etc
for (const label of GAMEPLAY.X_LABELS)
{
    KEYS[label] = [label.toLowerCase()];
}
// 1 -> [1, num1], etc
for (const label of GAMEPLAY.Y_LABELS)
{
    KEYS[label] = [label, `num${label}`];
}

const KEY_CONTEXT = 'keypad';

export class CoordinateKeypad extends Container
{
    private static _instance: CoordinateKeypad;
    public static get instance() { return CoordinateKeypad._instance; }

    private overlay: Sprite;
    private box: Container;
    private bg: UIBoxBG;
    private buttons: NineSliceButton[];
    private clear: Button;
    private resolveOpen: (result:string|typeof CLEAR)=>void;
    private disposables: DisposableGroup;

    private padding = 10;
    private margin = 15;
    private rowSize = 3;
    private bWidth = 70;
    private bHeight = 70;

    public tutorialBlocked = false;

    public static preload()
    {
        const loader = LoadContext.current;
        loader.add('GameAtlas', './assets/game/GameAtlas{size}.json');

        UIBoxBG.preload();
        NineSliceButton.preload();
        Button.preload();
    }

    constructor(vo: VOManager)
    {
        super();

        CoordinateKeypad._instance = this;
        this.visible = false;
        this.disposables = new DisposableGroup();

        const loader = LoadContext.current;

        this.overlay = new Sprite(Texture.WHITE);
        this.overlay.tint = 0x000000;
        this.overlay.alpha = 0.6;
        this.overlay.width = LAYOUT.MAX_WIDTH;
        this.overlay.height = LAYOUT.MAX_HEIGHT;
        this.addChild(this.overlay);
        this.overlay.interactive = true;
        this.overlay.on('pointertap', () => this.close());

        this.box = new Container();
        this.addChild(this.box);

        const padWidth = this.bWidth * this.rowSize + (this.rowSize - 1) * this.padding + this.margin * 2;
        this.bg = new UIBoxBG(padWidth, 100);
        this.box.addChild(this.bg);
        this.bg.interactive = true;// block clicks to the overlay

        const numButtons = Math.max(GAMEPLAY.X_LABELS.length, GAMEPLAY.Y_LABELS.length);
        this.buttons = [];
        for (let i = 0; i < numButtons; ++i)
        {
            const button = new NineSliceButton(
                {
                    texture: loader.getSpritesheet('GameAtlas').textures['coord_bg'],
                    frame: 5,
                    width: this.bWidth,
                    height: this.bHeight
                },
                {
                    content: '',
                    style: fonts.GameCoordButton
                }
            );
            const row = Math.floor(i / 3);
            const column = i % 3;
            button.position.set(
                (padWidth / 2) + ((column - 1) * (this.bWidth + this.padding)),
                this.margin + (row + 0.5) * this.bHeight + row * this.padding
            );
            this.box.addChild(button);
            this.buttons.push(button);
            this.disposables.add(new InteractiveWrapper(button, {
                onHover: () => {
                    vo.playHighlight(`CYB_Game_${button.label}`);
                },
                onTap: () => {
                    this.close(button.label);
                }
            }));
        }

        this.clear = new NineSliceButton(
            {
                texture: loader.getSpritesheet('GameAtlas').textures['btn_blank'],
                frame: 32,
                width: this.bWidth * 2,
                height: 65
            },
            {
                content: 'Clear',
                style: fonts.GameButton
            }
        );
        this.clear.position.set(padWidth / 2, 100);
        this.box.addChild(this.clear);
        this.disposables.add(new InteractiveWrapper(this.clear, {
            onHover: () => {
                vo.playHighlight(`CYB_Game_Clear`);
            },
            onTap: () => {
                this.close(CLEAR);
            }
        }));

        KeyboardJS.withContext(KEY_CONTEXT, () => {
            // add key to bail
            this.disposables.add(Disposable.fromKey('esc', null, () => {
                this.close();
            }));
            // add keys for clear
            this.disposables.add(Disposable.fromKey(['backspace', 'delete'], null, () => {
                this.close(CLEAR);
            }));
            // add key for each possible button label, we'll ignore improper inputs on press
            // disabling default up and down arrows to scroll the page
            for (const label in KEYS)
            {
                this.disposables.add(Disposable.fromKey(KEYS[label], null, this.onKey.bind(this, label)));
            }
        });
    }

    private onKey(label:string)
    {
        for (const button of this.buttons)
        {
            if (button.visible && button.label === label)
            {
                button.emit('pointertap');
                return;
            }
        }
        // entered a wrong character!
        if (parseInt(this.buttons[0].label) > 0)
        {
            this.emit('wrong-number');
        }
        else
        {
            this.emit('wrong-char');
        }
    }

    public get isNumbers()
    {
        return parseInt(this.buttons[0].label) > 0;
    }

    public get isLetters()
    {
        return ! this.isNumbers;
    }

    public get isActive()
    {
        return KeyboardJS.getContext() === KEY_CONTEXT;
    }

    public open(labels:string[], position:SimplePoint):Promise<string|typeof CLEAR>
    {
        const prom = this.enableKeyboard(labels);

        this.toLocal(position as any, null, position as any);
        this.box.position.set(position.x - this.bg.width / 2, position.y);
        this.visible = true;
        // activate keyboard keys
        FocusManager.instance.activateContext(this.buttons.filter(b => b.visible).concat(this.clear));
        this.emit('opened');

        return prom;
    }

    /**
     * Enables the keyboard, without showing the keypad itself.
     */
    public enableKeyboard(labels:string[]):Promise<string|typeof CLEAR>
    {
        // labels = ['1', '2', '3', '4'];
        if (KeyboardJS.getContext() === KEY_CONTEXT)
        {
            // NOTE: this will orphan the promise for the previous enableKeyboard
            return new Promise(resolve => this.resolveOpen = resolve);
        }
        // set up buttons because we use them for button enabled calculations
        let maxY = 0;
        for (let i = 0; i < this.buttons.length; ++i)
        {
            const button = this.buttons[i];
            if (i < labels.length)
            {
                button.visible = true;
                button.label = labels[i];
                maxY = button.y + button.height / 2;
            }
            else
            {
                button.visible = false;
            }
        }
        this.clear.position.y = maxY + this.padding + this.clear.height / 2;
        this.bg.height = this.clear.position.y + this.clear.height / 2 + this.margin;
        // emit opened so that we act as though we are showing
        this.emit('activated');
        // activate keyboard keys
        // NOTE: need to keep shift pressed when KeyboardJS clears all active keys
        const shiftHeld = (KeyboardJS as any).getLocale().pressedKeys.includes('shift');
        KeyboardJS.setContext(KEY_CONTEXT);
        if (shiftHeld)
        {
            (KeyboardJS as any).getLocale().pressedKeys.push('shift');
        }

        return new Promise(resolve => this.resolveOpen = resolve);
    }

    /**
     * Closes the visual keypad and disables the keyboard.
     */
    private close(result?:string|typeof CLEAR)
    {
        // check with keyboard disabling first because we need to make sure we don't
        // pop the context first
        // keyboard disabling has the tutorial check code
        if (!this.disableKeyboard(result))
        {
            return;
        }
        if (!this.visible)
        {
            return;
        }
        this.visible = false;
        FocusManager.instance.popContext();
    }

    /**
     * Disables the keyboard, if allowed.
     */
    public disableKeyboard(result?:string|typeof CLEAR)
    {
        // no clear or clicking off when required to do tutorial
        if (this.tutorialBlocked && (!result || result === CLEAR))
        {
            this.emit('tutorialBlocked');
            return false;
        }
        // Deactivate keyboard keys
        // NOTE: need to keep shift pressed when KeyboardJS clears all active keys
        const shiftHeld = (KeyboardJS as any).getLocale().pressedKeys.includes('shift');
        KeyboardJS.setContext('gameplay');
        if (shiftHeld)
        {
            (KeyboardJS as any).getLocale().pressedKeys.push('shift');
        }
        // emit closed before the open promise gets to complete, so that the keypad close hook gets
        // to re-enable the game
        this.emit('closed', result === CLEAR ? '' : result);
        this.resolveOpen(result);
        return true;
    }

    public destroy()
    {
        this.disposables.destroy();
        CoordinateKeypad._instance = null;
        super.destroy({children: true});
    }
}
